var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { Renderer } from './Renderer';
import { Rect, Circle, Ellipse, TextNode, Path, Line, Polyline, Arc, Polygon } from '../shapes/index';
import { isGradient, gradientToCss, shadowToCss } from './styles';
import { arcSectorPath } from './arcSector';
import { toHighContrastColor } from '../utils/a11y';
import { applyUiTheme, cssStageBackground, toThemeBackgroundCss, unwrapThemeBackgroundSrc } from '../components/uiTheme';
import { NATIVE_HTML_COMPONENTS, syncNativeAccordion, syncNativeButton, syncNativeCard, syncNativeCheckbox, syncNativeDialog, syncNativeInput, syncNativeMenu, syncNativeProgress, syncNativeRadio, syncNativeSlider, syncNativeStatusBar, syncNativeTable, syncNativeTabs, syncNativeTextarea, syncNativeToast, syncNativeToggle, syncNativeToolbar, syncNativeTooltip, syncNativeTree, } from './htmlComponents';
/** HTML/CSS fallback renderer with incremental DOM updates. */
var HTMLRenderer = /** @class */ (function (_super) {
    __extends(HTMLRenderer, _super);
    function HTMLRenderer() {
        var _this = _super.apply(this, __spreadArray([], __read(arguments), false)) || this;
        _this.nodeElements = new Map();
        _this.innerContainers = new Map();
        _this.seenIds = new Set();
        _this.uiTheme = {};
        return _this;
    }
    HTMLRenderer.prototype.init = function (container, options) {
        var _a, _b;
        this.width = options.width;
        this.height = options.height;
        this.background = options.background;
        this.highContrast = (_a = options.highContrast) !== null && _a !== void 0 ? _a : false;
        this.uiTheme = (_b = options.uiTheme) !== null && _b !== void 0 ? _b : {};
        this.root = document.createElement('div');
        this.root.className = 'lightdraw-html-root';
        this.applyRootStyles();
        container.appendChild(this.root);
    };
    /** Replace programmatic theme tokens (re-applied after each layout pass). */
    HTMLRenderer.prototype.setUiTheme = function (tokens) {
        this.uiTheme = __assign({}, tokens);
        this.applyThemeVars();
    };
    HTMLRenderer.prototype.applyThemeVars = function () {
        var _a;
        applyUiTheme(this.root, (_a = this.uiTheme) !== null && _a !== void 0 ? _a : {});
    };
    /** Update stage background (solid color or image). Re-applies root CSS. */
    HTMLRenderer.prototype.setStageBackground = function (value) {
        this.background = value;
        if (this.root)
            this.applyRootStyles();
    };
    HTMLRenderer.prototype.applyRootStyles = function () {
        this.root.style.position = 'relative';
        this.root.style.width = "".concat(this.width, "px");
        this.root.style.height = "".concat(this.height, "px");
        this.root.style.overflow = 'hidden';
        if (this.highContrast) {
            this.root.style.background = '#000';
            this.root.style.backgroundImage = '';
            this.root.classList.add('lightdraw-high-contrast');
            this.root.setAttribute('data-ld-high-contrast', 'true');
        }
        else {
            this.root.classList.remove('lightdraw-high-contrast');
            this.root.removeAttribute('data-ld-high-contrast');
            var src = unwrapThemeBackgroundSrc(this.background || '');
            if (src) {
                // Set longhands — jsdom / some engines drop shorthand with data: URLs
                this.root.style.backgroundColor = 'transparent';
                this.root.style.backgroundImage = toThemeBackgroundCss(this.background);
                this.root.style.backgroundSize = 'cover';
                this.root.style.backgroundPosition = 'center';
                this.root.style.backgroundRepeat = 'no-repeat';
            }
            else {
                this.root.style.backgroundImage = '';
                this.root.style.backgroundSize = '';
                this.root.style.backgroundPosition = '';
                this.root.style.backgroundRepeat = '';
                this.root.style.background = cssStageBackground(this.background || 'transparent');
            }
        }
        this.applyThemeVars();
    };
    HTMLRenderer.prototype.resize = function (width, height) {
        this.width = width;
        this.height = height;
        this.applyRootStyles();
        this.forceFullRedraw();
    };
    HTMLRenderer.prototype.getElement = function () {
        return this.root;
    };
    HTMLRenderer.prototype.render = function (root, _cameraMatrix) {
        this.applyRootStyles();
        this.seenIds.clear();
        this.syncGroup(root, this.root);
        this.pruneOrphans(this.root);
        this.clearDirty();
    };
    HTMLRenderer.prototype.toDataURL = function () {
        return '';
    };
    HTMLRenderer.prototype.destroy = function () {
        this.nodeElements.clear();
        this.innerContainers.clear();
        this.root.remove();
    };
    HTMLRenderer.prototype.shouldSyncWhenHidden = function (node) {
        var _a;
        var t = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.componentType;
        return t === 'tooltip' || t === 'menu' || t === 'dialog';
    };
    HTMLRenderer.prototype.syncGroup = function (group, parent) {
        var e_1, _a;
        group.sortChildren();
        try {
            for (var _b = __values(group.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                var child = _c.value;
                if (!child.visible && !this.shouldSyncWhenHidden(child))
                    continue;
                this.syncNode(child, parent);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    HTMLRenderer.prototype.resolveRole = function (node) {
        var _a;
        if (node.role)
            return node.role;
        var componentType = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.componentType;
        var roles = {
            button: 'button',
            checkbox: 'checkbox',
            slider: 'slider',
            toggle: 'switch',
            progressBar: 'progressbar',
            input: 'textbox',
            textarea: 'textbox',
            radio: 'radio',
            menu: 'menu',
            dialog: 'dialog',
            tabs: 'tablist',
            table: 'grid',
            tree: 'tree',
            toolbar: 'toolbar',
            toast: 'status',
            statusBar: 'status',
        };
        if (componentType && roles[componentType])
            return roles[componentType];
        return 'img';
    };
    HTMLRenderer.prototype.applyA11y = function (node, el) {
        var _a, _b, _c;
        var role = this.resolveRole(node);
        el.setAttribute('role', role);
        el.setAttribute('aria-label', node.name || ((_a = node.metadata) === null || _a === void 0 ? void 0 : _a.label) || node.type);
        if (node.focusable) {
            el.tabIndex = node.id === this.focusedNodeId ? 0 : -1;
        }
        else {
            el.removeAttribute('tabindex');
        }
        if (node.id === this.focusedNodeId) {
            el.classList.add('lightdraw-focused');
        }
        else {
            el.classList.remove('lightdraw-focused');
        }
        if (node.ariaChecked !== undefined) {
            el.setAttribute('aria-checked', String(node.ariaChecked));
        }
        else {
            el.removeAttribute('aria-checked');
        }
        if (node.ariaValueNow !== undefined) {
            el.setAttribute('aria-valuenow', String(node.ariaValueNow));
        }
        if (node.ariaValueMin !== undefined) {
            el.setAttribute('aria-valuemin', String(node.ariaValueMin));
        }
        if (node.ariaValueMax !== undefined) {
            el.setAttribute('aria-valuemax', String(node.ariaValueMax));
        }
        var live = (_b = node.ariaLive) !== null && _b !== void 0 ? _b : (_c = node.metadata) === null || _c === void 0 ? void 0 : _c.ariaLive;
        if (live && live !== 'off') {
            el.setAttribute('aria-live', live);
        }
        else {
            el.removeAttribute('aria-live');
        }
    };
    HTMLRenderer.prototype.nativeCtx = function () {
        var _this = this;
        return {
            nodeElements: this.nodeElements,
            seenIds: this.seenIds,
            focusedNodeId: this.focusedNodeId,
            applyA11y: function (node, el) { return _this.applyA11y(node, el); },
            applyUiClasses: function (node, el) { return _this.applyUiClasses(node, el); },
        };
    };
    HTMLRenderer.prototype.syncNativeComponent = function (node, parent, type) {
        var ctx = this.nativeCtx();
        switch (type) {
            case 'button':
                syncNativeButton(node, parent, ctx);
                return true;
            case 'checkbox':
                syncNativeCheckbox(node, parent, ctx);
                return true;
            case 'toggle':
                syncNativeToggle(node, parent, ctx);
                return true;
            case 'slider':
                syncNativeSlider(node, parent, ctx);
                return true;
            case 'radio':
                syncNativeRadio(node, parent, ctx);
                return true;
            case 'progressBar':
                syncNativeProgress(node, parent, ctx);
                return true;
            case 'card':
                syncNativeCard(node, parent, ctx);
                return true;
            case 'tabs':
                syncNativeTabs(node, parent, ctx);
                return true;
            case 'accordion':
                syncNativeAccordion(node, parent, ctx);
                return true;
            case 'table':
                syncNativeTable(node, parent, ctx);
                return true;
            case 'tree':
                syncNativeTree(node, parent, ctx);
                return true;
            case 'toolbar':
                syncNativeToolbar(node, parent, ctx);
                return true;
            case 'toast':
                syncNativeToast(node, parent, ctx);
                return true;
            case 'menu':
                syncNativeMenu(node, parent, ctx);
                return true;
            case 'dialog':
                syncNativeDialog(node, parent, ctx);
                return true;
            case 'tooltip':
                syncNativeTooltip(node, parent, ctx);
                return true;
            case 'statusBar':
                syncNativeStatusBar(node, parent, ctx);
                return true;
            default:
                return false;
        }
    };
    HTMLRenderer.prototype.syncNode = function (node, parent) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        var componentType = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.componentType;
        if (componentType === 'input') {
            syncNativeInput(node, parent, this.nativeCtx());
            return;
        }
        if (componentType === 'textarea') {
            syncNativeTextarea(node, parent, this.nativeCtx());
            return;
        }
        if (componentType && NATIVE_HTML_COMPONENTS.has(componentType)) {
            if (this.syncNativeComponent(node, parent, componentType))
                return;
        }
        var widgetType = (_b = node.metadata) === null || _b === void 0 ? void 0 : _b.widgetType;
        if (widgetType === 'dataTable') {
            syncNativeTable(node, parent, this.nativeCtx());
            return;
        }
        if (this.isVectorShape(node)) {
            this.syncVectorShape(node, parent);
            return;
        }
        var el = this.nodeElements.get(node.id);
        if (!el) {
            el = document.createElement('div');
            el.id = node.id;
            parent.appendChild(el);
            this.nodeElements.set(node.id, el);
        }
        else if (el.parentElement !== parent) {
            parent.appendChild(el);
        }
        this.applyA11y(node, el);
        this.applyUiClasses(node, el);
        var extra = '';
        if (node.shadow)
            extra += "box-shadow: ".concat(shadowToCss(node.shadow), ";");
        if (node.clip)
            extra += 'overflow: hidden;';
        el.style.cssText = "\n      position: absolute;\n      left: ".concat(node.x, "px;\n      top: ").concat(node.y, "px;\n      opacity: ").concat(node.opacity, ";\n      transform: rotate(").concat(node.rotation, "deg) scale(").concat(node.scaleX, ", ").concat(node.scaleY, ");\n      transform-origin: top left;\n      pointer-events: ").concat(node.listening ? 'auto' : 'none', ";\n      ").concat(node.zIndex !== 0 ? "z-index: ".concat(node.zIndex, ";") : '', "\n      ").concat(extra, "\n    ");
        this.applyShapeStyles(node, el);
        if (node instanceof TextNode) {
            this.applyTextBoxPosition(node, el);
        }
        this.seenIds.add(node.id);
        if ('children' in node) {
            var bounds = node.getBounds();
            var chartW = ((_d = (_c = node.metadata) === null || _c === void 0 ? void 0 : _c.chartWidth) !== null && _d !== void 0 ? _d : (_e = node.metadata) === null || _e === void 0 ? void 0 : _e.autoWidth);
            var chartH = ((_g = (_f = node.metadata) === null || _f === void 0 ? void 0 : _f.chartHeight) !== null && _g !== void 0 ? _g : (_h = node.metadata) === null || _h === void 0 ? void 0 : _h.autoHeight);
            if (((_j = node.metadata) === null || _j === void 0 ? void 0 : _j.componentType) && bounds.width > 0) {
                el.style.width = "".concat(bounds.width, "px");
                el.style.height = "".concat(Math.max(bounds.height, 1), "px");
            }
            else if (chartW && chartW > 0) {
                el.style.height = "".concat(Math.max(chartH !== null && chartH !== void 0 ? chartH : chartW, 1), "px");
                el.style.width = "".concat(chartW, "px");
            }
            var inner = this.innerContainers.get(node.id);
            if (!inner) {
                inner = document.createElement('div');
                inner.style.cssText = 'position:relative;width:100%;height:100%;';
                el.appendChild(inner);
                this.innerContainers.set(node.id, inner);
            }
            this.syncGroup(node, inner);
        }
    };
    HTMLRenderer.prototype.applyUiClasses = function (node, el) {
        var _a;
        var t = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.componentType;
        if (t) {
            el.classList.add('lightdraw-ui', "lightdraw-ui--".concat(t));
        }
        if (node.focusable || t === 'button' || t === 'checkbox' || t === 'radio' || t === 'toggle') {
            el.classList.add('lightdraw-interactive');
        }
    };
    HTMLRenderer.prototype.isVectorShape = function (node) {
        return (node instanceof Line ||
            node instanceof Polyline ||
            node instanceof Arc ||
            node instanceof Polygon ||
            node instanceof Path);
    };
    HTMLRenderer.prototype.syncVectorShape = function (node, parent) {
        var svg = this.nodeElements.get(node.id);
        if (!svg || svg.tagName !== 'svg') {
            svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.id = node.id;
            parent.appendChild(svg);
            this.nodeElements.set(node.id, svg);
        }
        else if (svg.parentElement !== parent) {
            parent.appendChild(svg);
        }
        this.seenIds.add(node.id);
        var b = node.getBounds();
        var pad = Math.max(Math.ceil(node.strokeWidth || 0), 2) + 1;
        var w = Math.max(b.width + pad * 2, 1);
        var h = Math.max(b.height + pad * 2, 1);
        svg.setAttribute('aria-hidden', 'true');
        svg.style.cssText = "\n      position: absolute;\n      left: ".concat(node.x + b.x - pad, "px;\n      top: ").concat(node.y + b.y - pad, "px;\n      width: ").concat(w, "px;\n      height: ").concat(h, "px;\n      overflow: visible;\n      pointer-events: ").concat(node.listening ? 'auto' : 'none', ";\n      opacity: ").concat(node.opacity, ";\n    ");
        while (svg.firstChild)
            svg.removeChild(svg.firstChild);
        var ox = pad - b.x;
        var oy = pad - b.y;
        var strokeColor = node.stroke ? this.strokeToCss(node.stroke) : '#64748b';
        var sw = String(node.strokeWidth || 2);
        var fillColor = node.fill && node.fill !== null && node.fill !== 'transparent'
            ? this.fillToCss(node.fill)
            : 'none';
        if (node instanceof Line) {
            var line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', String(ox));
            line.setAttribute('y1', String(oy));
            line.setAttribute('x2', String(node.x2 + ox));
            line.setAttribute('y2', String(node.y2 + oy));
            line.setAttribute('stroke', strokeColor);
            line.setAttribute('stroke-width', sw);
            line.setAttribute('stroke-linecap', node.lineCap);
            svg.appendChild(line);
        }
        else if (node instanceof Polyline) {
            var pts = node.points.map(function (v, i) { return (i % 2 === 0 ? v + ox : v + oy); }).join(' ');
            var poly = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
            poly.setAttribute('points', pts);
            poly.setAttribute('stroke', strokeColor);
            poly.setAttribute('stroke-width', sw);
            poly.setAttribute('fill', fillColor);
            poly.setAttribute('stroke-linejoin', node.lineJoin);
            poly.setAttribute('stroke-linecap', node.lineCap);
            svg.appendChild(poly);
        }
        else if (node instanceof Polygon) {
            var pts = node.points.map(function (v, i) { return (i % 2 === 0 ? v + ox : v + oy); }).join(' ');
            var poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
            poly.setAttribute('points', pts);
            poly.setAttribute('stroke', strokeColor);
            poly.setAttribute('stroke-width', sw);
            poly.setAttribute('fill', fillColor);
            svg.appendChild(poly);
        }
        else if (node instanceof Arc) {
            var cx = node.radius + ox;
            var cy = node.radius + oy;
            var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', arcSectorPath(cx, cy, node.radius, node.startAngle, node.endAngle, node.innerRadius, node.counterClockwise));
            path.setAttribute('stroke', strokeColor);
            path.setAttribute('stroke-width', sw);
            path.setAttribute('fill', fillColor);
            path.setAttribute('stroke-linecap', node.lineCap);
            svg.appendChild(path);
        }
        else if (node instanceof Path) {
            var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', node.d);
            path.setAttribute('stroke', node.stroke ? strokeColor : 'none');
            path.setAttribute('stroke-width', sw);
            path.setAttribute('fill', fillColor);
            path.setAttribute('transform', "translate(".concat(ox, ", ").concat(oy, ")"));
            svg.appendChild(path);
        }
    };
    HTMLRenderer.prototype.fillToCss = function (fill) {
        if (!fill)
            return 'transparent';
        if (typeof fill === 'string') {
            return this.highContrast ? toHighContrastColor(fill, 'fill') : fill;
        }
        if (isGradient(fill))
            return gradientToCss(fill);
        return 'transparent';
    };
    HTMLRenderer.prototype.strokeToCss = function (stroke) {
        if (!stroke || typeof stroke !== 'string')
            return '#000';
        return this.highContrast ? toHighContrastColor(stroke, 'stroke') : stroke;
    };
    HTMLRenderer.prototype.applyTextBoxPosition = function (node, el) {
        var _a, _b;
        var boxW = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.textBoxWidth;
        var centerY = (_b = node.metadata) === null || _b === void 0 ? void 0 : _b.textBoxCenterY;
        if (boxW && boxW > 0 && node.textAlign === 'center') {
            el.style.width = "".concat(boxW, "px");
            el.style.textAlign = 'center';
            el.style.left = "".concat(node.x - boxW / 2, "px");
            if (centerY !== undefined) {
                el.style.top = "".concat(centerY - node.fontSize / 2, "px");
                el.style.height = "".concat(node.fontSize, "px");
                el.style.lineHeight = "".concat(node.fontSize, "px");
            }
            el.style.zIndex = String(Math.max(node.zIndex, 902));
            return;
        }
        if (node.textAlign && node.textAlign !== 'left') {
            el.style.textAlign = node.textAlign;
            var b = node.getBounds();
            el.style.width = "".concat(Math.max(b.width, node.fontSize), "px");
        }
    };
    HTMLRenderer.prototype.applyShapeStyles = function (node, el) {
        if (node instanceof Rect) {
            el.style.width = "".concat(node.width, "px");
            el.style.height = "".concat(node.height, "px");
            el.style.background = this.fillToCss(node.fill);
            el.style.border = node.stroke
                ? "".concat(node.strokeWidth, "px solid ").concat(this.strokeToCss(node.stroke))
                : 'none';
            el.style.borderRadius = node.cornerRadius ? "".concat(node.cornerRadius, "px") : '';
        }
        else if (node instanceof Circle) {
            el.style.width = "".concat(node.radius * 2, "px");
            el.style.height = "".concat(node.radius * 2, "px");
            el.style.borderRadius = '50%';
            el.style.background = this.fillToCss(node.fill);
            el.style.border = node.stroke
                ? "".concat(node.strokeWidth, "px solid ").concat(this.strokeToCss(node.stroke))
                : 'none';
        }
        else if (node instanceof Ellipse) {
            el.style.width = "".concat(node.radiusX * 2, "px");
            el.style.height = "".concat(node.radiusY * 2, "px");
            el.style.borderRadius = '50%';
            el.style.background = this.fillToCss(node.fill);
        }
        else if (node instanceof TextNode) {
            el.textContent = node.text;
            el.style.font = "".concat(node.fontWeight, " ").concat(node.fontSize, "px ").concat(node.fontFamily);
            el.style.color = this.fillToCss(node.fill);
            el.style.background = 'transparent';
            el.style.whiteSpace = 'pre';
            el.style.lineHeight = "".concat(Math.max(node.fontSize + 2, 12), "px");
        }
        else if (node instanceof Path) {
            // Vector paths use SVG via syncVectorShape
            var b = node.getBounds();
            el.style.width = "".concat(Math.max(b.width, 1), "px");
            el.style.height = "".concat(Math.max(b.height, 1), "px");
            el.style.background = 'transparent';
        }
    };
    HTMLRenderer.prototype.pruneOrphans = function (parent) {
        var e_2, _a, e_3, _b;
        var toRemove = [];
        try {
            for (var _c = __values(Array.from(parent.children)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var child = _d.value;
                if (child.id && !this.seenIds.has(child.id)) {
                    toRemove.push(child);
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_2) throw e_2.error; }
        }
        try {
            for (var toRemove_1 = __values(toRemove), toRemove_1_1 = toRemove_1.next(); !toRemove_1_1.done; toRemove_1_1 = toRemove_1.next()) {
                var el = toRemove_1_1.value;
                this.nodeElements.delete(el.id);
                this.innerContainers.delete(el.id);
                el.remove();
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (toRemove_1_1 && !toRemove_1_1.done && (_b = toRemove_1.return)) _b.call(toRemove_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
    };
    HTMLRenderer.prototype.drawGroup = function (_group) { };
    HTMLRenderer.prototype.drawRect = function (_node) { };
    HTMLRenderer.prototype.drawCircle = function (_node) { };
    HTMLRenderer.prototype.drawText = function (_node) { };
    return HTMLRenderer;
}(Renderer));
export { HTMLRenderer };
